<template>
    <div class="form-container">
        <template v-if="isAce">
            <div v-if="type == 'avoid'" class="form">
                <div v-if="!isAvoided" class="inputs-container">
                    <h2>Are you sure that you want to avoid this vendor?</h2>
                    <div class="warning">
                        <p>
                            This will remove the vendor from this and all future search results.
                            It will also add this listing to your "avoided" list which can be accessed and managed
                            <a class="warning-link" href="/admin" title="Admin">here</a>.
                        </p>
                    </div>
                    <div class="form-fields">
                        <label>Add note to listing (optional):</label>
                        <div>
                            <textarea v-model="note"></textarea>
                        </div>
                    </div>
                </div>
                <div v-if="isAvoided" class="inputs-container">
                    <h2>Are you sure that you want to remove this vendor from your avoided list?</h2>
                    <div class="warning">
                        <p>
                            This will re-add the vendor on future search results.
                            It will also remove this listing from your "avoided" list.
                        </p>
                    </div>
                </div>

                <div class="form-buttons">
                    <button class="cancel-btn" @click="closeForm">Cancel</button>
                    <button v-if="isAvoided" class="forward-btn" @click="clearPreferred">Remove from avoided list</button>
                    <button v-if="!isAvoided" class="forward-btn" @click="avoid">Avoid vendor</button>
                </div>
            </div>
            <div v-if="type == 'favorite'" class="form">
                <div class="inputs-container">
                    <h2>Are you sure that you want to add this vendor to your favorites list?</h2>
                    <div class="warning">
                        <p>
                            This will make the vendor display at the top of all future search results in the area.
                            It will also add this listing to your "preferred" list which can be accessed and managed
                            <a class="warning-link" href="/admin" title="Admin">here</a>.
                        </p>
                    </div>
                    <div class="form-fields">
                        <label>Add note to listing (optional):</label>
                        <div>
                            <textarea v-model="note"></textarea>
                        </div>
                    </div>
                </div>

                <div class="form-buttons">
                    <button class="cancel-btn" @click="closeForm">Cancel</button>
                    <button class="forward-btn" @click="favorite">Add to favorites</button>
                </div>
            </div>
            <div v-if="type == 'edit'" class="form edit-form">
                <div class="inputs-container">

                    <h2 style="margin-bottom: 0">Edit Settings</h2>
                    <div class="form-fields">
                        <div>
                            <label>Priority</label>
                            <select v-model="priority">
                                <option :value="undefined">No Priority</option>
                                <option :value="1">First</option>
                                <option :value="2">Second</option>
                                <option :value="3">Third</option>
                                <option :value="4">Fourth</option>
                                <option :value="5">Fifth</option>
                                <option :value="6">Sixth</option>
                                <option :value="7">Seventh</option>
                                <option :value="8">Eighth</option>
                                <option :value="9">Ninth</option>
                            </select>
                        </div>
                        <div>
                            <label>Search Radius</label>
                            <select v-model="radius">
                                <option :value="undefined">Default</option>
                                <option :value="5">5 Miles</option>
                                <option :value="10">10 Miles</option>
                                <option :value="15">15 Miles</option>
                                <option :value="20">20 Miles</option>
                                <option :value="25">25 Miles</option>
                                <option :value="50">50 Miles</option>
                                <option :value="100">100 Miles</option>
                            </select>
                        </div>
                        <div>
                            <label>Services</label>
                            <div class="service dropdown" v-click-outside="closeDropdown">
                                <div class="wrapper">
                                    <div class="selected-service" v-for="service in selectedServices">
                                        <span>{{ service.name }}</span>
                                        <a class="servise-close" href="#" @click.prevent="deselectService(service.code)">x</a>
                                    </div>
                                    <input name="service" class="service-input" v-model="serviceInput" :placeholder="servicePlaceholder" @focus="focusService" type="text" />
                                    <i class="service-icon" v-if="selectedServices.length === 0">
                                        <fa-icon :icon="['fas', 'screwdriver-wrench']" aria-hidden="true"></fa-icon>
                                    </i>

                                </div>
                                <div v-if="openDropDownId === 'service'" class="content service-content">
                                    <div class="close-service-content"><a class="service-close" href="#" @click.prevent="closeDropdown">x</a></div>
                                    <ul>
                                        <li><span>Service</span></li>
                                        <li v-for="service in filteredServices">
                                            <a href="#" @click.prevent="selectService(service.code)">{{ service.name }}</a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div>
                            <label>Add note to listing (optional):</label>
                            <div>
                                <textarea v-model="note"></textarea>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="form-buttons">
                    <button class="cancel-btn" @click="closeForm">Cancel</button>
                    <button class="clear-btn" @click="clearPreferred">Remove from favorites</button>
                    <button class="forward-btn" @click="saveSettings">Save settings</button>
                </div>
            </div>
        </template>
        <template v-else>
            <template v-if="type == 'avoid'">
                <marketing-action class="py-0" :more-info-url="moreInfoUrl">
                    <template #heading>
                        <h2>Avoid this vendor</h2>
                        <p>Remove this vendor from all future searches. <br />They will also be added to a "do not use" list that you can manage.</p>
                    </template>
                    <template #subheading>
                        <h3>This is a premium feature available in paid accounts</h3>
                        <h4><span class="capital">Avoid this vendor</span> and speed up your quality searches!</h4>
                    </template>
                </marketing-action>
            </template>
            <template v-else>
                <marketing-action class="py-0" :more-info-url="moreInfoUrl">
                    <template #heading>
                        <h2>Save favorite</h2>
                        <p>Put your best vendors at the top of the search results in this area. <br />They will also be added to a "preferred" list that you can manage.</p>
                    </template>
                    <template #subheading>
                        <h3>This is a premium feature available in paid accounts</h3>
                        <h4>Save <strong>favorite vendors</strong> and find them faster next time!</h4>
                    </template>
                </marketing-action>
            </template>
        </template>
    </div>
</template>

<script>import script from './ListingPreference';
    export default script;</script>

<style lang="less" scoped>
    @import "./ListingPreference.less";
</style>

